import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button, EmptyState, SectionHeader, Spinner } from "../../../components/ui";
import { assetsErrorOf } from "../services/activosApi";

// ============================================================================
// Piezas comunes de las pantallas de Activos (estándar ui-wayak). Archivo
// común, idéntico en CC-CONSOLE y WAYAK.
// ============================================================================

interface SectionFrameProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  filters?: ReactNode;
  children: ReactNode;
}

/** Encabezado de sección + cuerpo con scroll (`p-3 sm:p-6`). */
export function SectionFrame({ title, subtitle, actions, filters, children }: SectionFrameProps) {
  return (
    <div className="flex h-full w-full flex-col">
      <SectionHeader title={title} subtitle={subtitle} actions={actions} filters={filters} />
      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto w-full max-w-6xl space-y-5">{children}</div>
      </div>
    </div>
  );
}

/** Loader único de la plataforma. */
export function Loader() {
  return (
    <div className="flex min-h-64 items-center justify-center py-12" data-testid="activos-loading">
      <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
    </div>
  );
}

/**
 * Error de una consulta. Distingue «sin acceso» (403 `ENTITLEMENT_REQUIRED`),
 * «sin compañía» (403 `COMPANY_SCOPE_FORBIDDEN`) y «no encontrado» del resto.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation("activos");
  const e = assetsErrorOf(error);
  if (e.code === "ENTITLEMENT_REQUIRED" && e.status === 403) {
    return <EmptyState icon="ri-lock-2-line" title={t("errors.noAccess.title")} description={t("errors.noAccess.text")} />;
  }
  if (e.code === "COMPANY_SCOPE_FORBIDDEN") {
    return <EmptyState icon="ri-building-line" title={t("errors.noCompany.title")} description={t("errors.noCompany.text")} />;
  }
  if (e.status === 404) {
    return <EmptyState icon="ri-search-line" title={t("errors.notFound.title")} description={t("errors.notFound.text")} />;
  }
  return (
    <EmptyState
      icon="ri-error-warning-line"
      title={t("errors.load.title")}
      description={e.message || t("errors.load.text")}
      action={
        onRetry ? (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            {t("common.retry")}
          </Button>
        ) : undefined
      }
    />
  );
}

/** Fila etiqueta/valor de una ficha. */
export function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <span className="shrink-0 text-content-muted">{label}</span>
      <span className="min-w-0 text-right text-content">{children}</span>
    </div>
  );
}

/** Tarjeta de conteo del resumen. */
export function StatCard({
  icon,
  label,
  value,
  hint,
  testId,
}: {
  icon: string;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  testId?: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-line bg-card p-4" data-testid={testId}>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
        <i className={`${icon} text-lg`} />
      </div>
      <div className="min-w-0">
        <p className="truncate text-xs text-content-muted">{label}</p>
        <p className="text-lg font-semibold text-content">{value}</p>
        {hint ? <p className="truncate text-xs text-content-faint">{hint}</p> : null}
      </div>
    </div>
  );
}

/** Título de bloque dentro de un formulario. */
export function FormBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-4 space-y-3 first:mt-0">
      <h3 className="border-b border-line pb-2 text-sm font-semibold text-content">{title}</h3>
      {children}
    </div>
  );
}

/** Pie de formulario 50/50 en píldora (ui-wayak §2). */
export function FormFooter({
  onCancel,
  submitting,
  submitLabel,
  submitTestId,
}: {
  onCancel: () => void;
  submitting: boolean;
  submitLabel: string;
  submitTestId?: string;
}) {
  const { t } = useTranslation("activos");
  return (
    <div className="mt-5 flex gap-3 border-t border-line pt-4">
      <Button variant="secondary" size="md" shape="pill" className="flex-1" onClick={onCancel}>
        {t("common.cancel")}
      </Button>
      <Button type="submit" size="md" shape="pill" className="flex-1" loading={submitting} disabled={submitting} data-testid={submitTestId}>
        {submitting ? t("common.saving") : submitLabel}
      </Button>
    </div>
  );
}

/** Spinner en línea (botones «Cargar más»). */
export function InlineSpinner() {
  return <Spinner size="sm" className="text-primary-500" />;
}
